import React from "react";
import styled from "@emotion/styled";

import Button, { CombinedButtonProps } from "components/ui/button/Button";
import ButtonSplit from "components/ui/button_split/ButtonSplit";
import type { ComboboxData } from "components/ui/combobox/types";
import EllipsisText from "components/ui/tooltip/EllipsisText";
import TooltipComponent from "components/ui/tooltip/Tooltip";
import { useSubscription } from "lib/hooks/subscription";
import { usePaywall } from "lib/providers/Paywall";
import { convertFeatureTypeToDataType, FeatureType } from "../../../paywall/paywallUtils";
import { Buyer } from "./BuyersCombobox";
import { OptionIconMap } from "./ComboboxOption";
import { Supplier } from "./SuppliersCombobox";

export type EntitySelectedType = Buyer | Supplier | { id: string; name: string };

export type LinkButtonProps = {
  href?: string;
  tooltip?: string;
  paywall?: { featureType: FeatureType; contextSource?: string };
};

export function SelectedEntityCard({
  entity,
  combobox,
  setIsOpen,
  setEntityClickerId,
  clickedEntityId,
  dataType,
  entityButtonProps,
  linkButtonProps,
}: {
  entity: EntitySelectedType;
  combobox: ComboboxData;
  setIsOpen: ((isOpen: boolean) => void) | null;
  entityButtonProps?: { iconColor?: string; iconBackgroundColor?: string };
  linkButtonProps: LinkButtonProps;
  dataType: keyof typeof OptionIconMap;
  setEntityClickerId: (id: string | null) => void;
  clickedEntityId: string | null;
}) {
  const { openPaywallModal } = usePaywall();
  const subscription = useSubscription();

  const paywallProps = linkButtonProps.paywall;
  const isPaywalled =
    !!paywallProps &&
    !subscription.hasDataTypes(convertFeatureTypeToDataType(paywallProps.featureType));

  const linkButton = () => {
    const label = `View ${entity.name}`;

    if (linkButtonProps.href) {
      return (
        <Button
          as="a"
          href={linkButtonProps.href}
          target="_blank"
          icon="chevronRight"
          buttonType="primary"
          buttonStyle="inverse"
          onClick={(e) => {
            if (isPaywalled) {
              e.preventDefault();
              openPaywallModal(paywallProps.featureType, {
                contextSource: paywallProps.contextSource,
              });
            }
          }}
          aria-label={label}
        />
      );
    }

    return (
      <Button
        icon="chevronRight"
        buttonType="primary"
        buttonStyle="inverse"
        disabled
        aria-label={label}
      />
    );
  };

  return (
    <EntityButtonCardButtonSplit secondaryType="a">
      <EntityButton
        buttonType="primary"
        buttonStyle="inverse"
        icon={OptionIconMap[dataType]}
        iconProps={{
          color: entityButtonProps?.iconColor ?? "sysPrimaryDefault",
          background: { color: entityButtonProps?.iconBackgroundColor ?? "sysPrimarySubtle" },
        }}
        justifyContent="start"
        width="100%"
        data-state={combobox.open && clickedEntityId === entity.id ? "open" : "closed"}
        onClick={() => {
          if (!combobox.open) {
            combobox.setFilterValue("");
            setEntityClickerId(entity.id);
          } else if (clickedEntityId === entity.id) {
            setEntityClickerId(null);
          } else {
            setEntityClickerId(entity.id);
          }
          const newOpenState = !combobox.open || clickedEntityId !== entity.id;
          combobox.setOpen(newOpenState);
          setIsOpen?.(newOpenState);
        }}
        aria-label={`Edit ${dataType}s`}
      >
        <EllipsisText color="inherit">{entity.name}</EllipsisText>
      </EntityButton>
      <TooltipComponent content={linkButtonProps.tooltip}>{linkButton()}</TooltipComponent>
    </EntityButtonCardButtonSplit>
  );
}

const EntityButtonCardButtonSplit = styled(ButtonSplit)({
  width: "100%",
  height: "32px",
  minWidth: 0,
});

const EntityButton = styled(Button)({
  minWidth: 0,
  padding: "4px 8px",
}) as unknown as CombinedButtonProps;
